import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { alpha, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import TnoMediaLabLogo, { MEDIA_LAB_TAGLINES } from 'components/TnoMediaLabLogo';
import FloatingDecor from '../../CherrySoftware/components/FloatingDecor';
import IconBadge from '../../CherrySoftware/components/IconBadge';
import { hubHero } from '../mediaLabHubContent';

const HERO_OUTER_PADDING_X = { xs: 2.5, sm: 3.5, md: 4.5 };
const HERO_OUTER_PADDING_Y = { xs: 2, sm: 3, md: 4 };
const PANEL_PADDING_X = { xs: 2.5, sm: 3, md: 3.5 };
const PANEL_PADDING_Y = { xs: 2.5, sm: 3, md: 3.5 };

const HubHero = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <Box
      position="relative"
      overflow="hidden"
      minHeight={{ md: '88vh' }}
      display="flex"
      alignItems="center"
      paddingX={HERO_OUTER_PADDING_X}
      paddingY={HERO_OUTER_PADDING_Y}
      sx={{
        background: `
          radial-gradient(ellipse 80% 60% at 15% 20%, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 55%),
          radial-gradient(ellipse 70% 50% at 90% 10%, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 50%),
          radial-gradient(ellipse 50% 40% at 70% 90%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 45%),
          linear-gradient(160deg, ${theme.palette.alternate.dark} 0%, ${theme.palette.background.default} 45%, ${theme.palette.background.paper} 100%)
        `,
      }}
    >
      <FloatingDecor
        icons={[
          { id: 'web', icon: 'web', top: '14%', right: '10%', size: 44, color: 'primary', animate: true },
          { id: 'voice', icon: 'voice', bottom: '20%', right: '16%', size: 36, color: 'secondary', animate: true, delay: '1.5s' },
        ]}
      />

      <Grid
        container
        spacing={{ xs: 3, md: 5 }}
        alignItems="center"
        position="relative"
        zIndex={1}
        sx={{
          maxWidth: { sm: 720, md: 1280, lg: 1400 },
          width: '100%',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12} md={7}>
          <Box
            data-aos={isMd ? 'fade-right' : 'fade-up'}
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
          >
            <TnoMediaLabLogo
              tagline={MEDIA_LAB_TAGLINES.hub}
              alt={hubHero.brand}
              height={{ xs: 48, md: 64 }}
              width="auto"
              sx={{ mb: { xs: 3, md: 4 } }}
            />
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.12,
                fontSize: { xs: '1.85rem', sm: '2.4rem', md: '2.85rem' },
                mb: 3,
                maxWidth: 820,
              }}
            >
              {hubHero.title}
            </Typography>
            <Typography
              color="textSecondary"
              sx={{
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                lineHeight: 1.75,
                mb: 4,
                maxWidth: 640,
              }}
            >
              {hubHero.subtitle}
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              style={{ gap: 12 }}
            >
              <Button
                variant="contained"
                color="secondary"
                size="large"
                href={hubHero.ctaPrimaryHref}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 3.5,
                  py: 1.5,
                  fontWeight: 800,
                  borderRadius: 2,
                  boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.35)}`,
                }}
              >
                {hubHero.ctaPrimary}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                href={hubHero.ctaSecondaryHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ px: 3.5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
              >
                {hubHero.ctaSecondary}
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            data-aos={isMd ? 'fade-left' : 'fade-up'}
            position="relative"
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: theme.palette.secondary.main,
                    boxShadow: `0 0 0 6px ${alpha(theme.palette.secondary.main, 0.18)}`,
                    animation: 'hub-live 1.8s ease-in-out infinite',
                    '@keyframes hub-live': {
                      '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(1.15)', opacity: 0.75 },
                    },
                  }}
                />
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 800, letterSpacing: 1.3, color: 'secondary.main', lineHeight: 1 }}
                >
                  {hubHero.panelLabel}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                {hubHero.shortcuts.length} puertas
              </Typography>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2.5, lineHeight: 1.25 }}>
              {hubHero.panelTitle}
            </Typography>

            <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
              {hubHero.shortcuts.map((item, index) => {
                const isExternal = Boolean(item.external || /^https?:\/\//i.test(item.href));
                const linkProps = isExternal
                  ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                  : { component: RouterLink, to: item.href };
                const accent = theme.palette[item.color || 'primary'].main;

                return (
                  <Box
                    key={item.id}
                    {...linkProps}
                    data-aos="fade-up"
                    data-aos-delay={80 * index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      pr: 1.75,
                      textDecoration: 'none',
                      color: 'inherit',
                      borderRadius: 2.5,
                      bgcolor: alpha(theme.palette.common.white, 0.72),
                      border: `1px solid ${alpha(accent, 0.14)}`,
                      boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.04)}`,
                      backdropFilter: 'blur(10px)',
                      transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateX(6px)',
                        bgcolor: alpha(theme.palette.common.white, 0.95),
                        borderColor: alpha(accent, 0.35),
                        boxShadow: `0 14px 32px ${alpha(accent, 0.16)}`,
                        '& .hub-arrow': {
                          transform: 'translateX(4px)',
                          color: accent,
                        },
                      },
                    }}
                  >
                    <IconBadge
                      icon={item.icon}
                      color={item.color || 'primary'}
                      variant="gradient"
                      size={42}
                      rounded="12px"
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', lineHeight: 1.25 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                        {item.hint}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      className="hub-arrow"
                      sx={{
                        fontSize: 20,
                        color: alpha(theme.palette.text.primary, 0.35),
                        transition: 'transform 0.22s ease, color 0.22s ease',
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HubHero;
